import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, Row, Col,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormFeedback, Input, Label, Button, Spinner
} from "reactstrap";
import { useFormik } from 'formik';
import * as Yup from "yup";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { getCompanyDetails, updateCompany } from '../../../slices/auth/settings/thunk';
import { createSelector } from 'reselect';
import UploadLogo from '../../../Components/Common/UploadLogo';
import checkPermission from '../../../Components/Hooks/PermissionHook';
import ErrorAlert from '../../../Components/Common/ErrorAlert';
import { resetCompanyErr } from '../../../slices/auth/settings/reducer';
import { toggleCompanyModal } from '../../../slices/modals/reducer';
import Preloader from '../../../Components/Common/Preloader';
import CustomSelect from '../../../Components/Common/CustomSelect';
import CustomSelectWithSearch from '../../../Components/Common/CustomSelectWithSearch';
import country from '../../../common/country';
import { industryType } from '../../../common/industryType';


function CompanyEditModal() {
  const dispatch = useDispatch();

  const selectState = createSelector(state => state, state => ({
    companyModal: state.Modal.companyModal,
    companyInfo: state.MenuData.client,
    companyErr: state.Settings.companyErr,
    companyErrMsg: state.Settings.companyErrMsg,
    billing: state.Settings.billing
  }));
  const { companyModal, companyInfo, companyErr, companyErrMsg, billing } = useSelector(selectState);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: (companyInfo?._id || null),
      logo: (companyInfo?.logo || ""),
      clientName: (companyInfo?.clientName || ""),
      contactPerson: (companyInfo?.contactPerson || ""),
      industryType: (companyInfo?.industryType || ""),
      country: (companyInfo?.country || ""),
      // city: (companyInfo?.city || ""),
      webSite: (companyInfo?.webSite || ""),
      contactEmail: (companyInfo?.contactEmail || ""),
      // employeeCount: (companyInfo?.employeeCount || ''),
      // since: (companyInfo?.since || ''),
      information: (companyInfo?.information || ""),
      billingName: (billing?.billingName || ""),
      taxNumber: (billing?.taxNumber || ""),
      billingId: (billing?._id || "65ce0c1c8eca5540d130ea02"),
      billingAddress: (billing?.billingAddress || ""),
      billingCountry: (billing?.billingCountry || ""),
      phoneNumber: (billing?.phoneNumber || "")
    },
    validationSchema: Yup.object({
      clientName: Yup.string().required("Please enter a company name"),
      contactPerson: Yup.string().required("Please enter a contact person"),
      industryType: Yup.mixed().oneOf(industryType).required("Please select industry type"),
      country: Yup.mixed().required("Please select country"),
      // city: Yup.mixed().oneOf(["Stockholm", "Oslo", "Helsinki", "London"]).required("Please select city"),
      webSite: Yup.string().required('Website is required'),
      contactEmail: Yup.string().email('Please enter a valid email address').required("Please enter contact email"),
      // employeeCount: Yup.number().required('Please enter company employee count.'),
      // since: Yup.number().required('Please enter company since.'),
      information: Yup.string().required("Please enter company information"),
      billingName: Yup.string().required("Please enter billing name"),
      taxNumber: Yup.string().required("Please enter tax number"),
      billingAddress: Yup.string().required("Please enter billing address"),
      billingCountry: Yup.string().required("Please select billing country"),
      phoneNumber: Yup.string().required("Please enter phone number"),
    }),
    onSubmit: async (values) => {
      const isUpdated = await dispatch(updateCompany(values));
      if (isUpdated) {
        toggleModal();
      }
    }
  })

  function handleChange(key, value) {
    validation.setFieldTouched(key, true);
    validation.setFieldValue(key, value);
  }

  /**
  * Toggles the visibility of the company modal.
  */
  function toggleModal() {
    dispatch(toggleCompanyModal());
    setTimeout(() => {
      validation.resetForm();
    }, 200);
  }
  return (
    <Modal isOpen={companyModal} toggle={toggleModal} centered size='lg'>
      <ModalHeader className="p-3" toggle={toggleModal}>Edit Company</ModalHeader>
      <Form className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}>
        <ModalBody>
          <React.Fragment>
            <Row>
              <Col style={{ display: "none" }}>
                <input type='hidden' name='id' defaultValue={companyInfo?.id} />
              </Col>
              <Col>
                <div className="text-center pb-4">
                  <UploadLogo src={(validation.values.logo)}
                    onChange={(logo) => { validation.setValues({ ...validation.values, logo: logo }) }} />
                  <p className="mb-0 fw-normal">Company Logo</p>
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <ErrorAlert errorState={companyErr} errorMessage={companyErrMsg} resetError={resetCompanyErr} />
              </Col>
            </Row>

            <Row>
              <Col>
                <div id="modal-id">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="clientName" className="form-label">Name</Label>
                    {validation.touched.clientName && !validation.errors.clientName ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.clientName && validation.errors.clientName ? (
                      <FormFeedback type="invalid">{validation.errors.clientName}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type="text" id="clientName" name="clientName"
                    placeholder="Please enter company name"
                    className={"form-control " + classnames({ "is-valid": validation.touched.clientName && !validation.errors.clientName })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.clientName && validation.errors.clientName ? true : false}
                    value={validation.values.clientName}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col sm="12" md="6">
                <div className="mt-3">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="contactPerson" className="form-label">Contact Person</Label>
                    {validation.touched.contactPerson && !validation.errors.contactPerson ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.contactPerson && validation.errors.contactPerson ? (
                      <FormFeedback type="invalid">{validation.errors.contactPerson}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type="text" id="contactPerson" name="contactPerson"
                    placeholder="Please enter contact person"
                    className={"form-control " + classnames({ "is-valid": validation.touched.contactPerson && !validation.errors.contactPerson })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.contactPerson && validation.errors.contactPerson ? true : false}
                    value={validation.values.contactPerson}
                  />
                </div>
              </Col>
              <Col sm="12" md="6">
                <div className="mt-3">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="industryType" className="form-label">Industry Type</Label>
                    {validation.touched.industryType && !validation.errors.industryType ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.industryType && validation.errors.industryType ? (
                      <FormFeedback type="invalid">{validation.errors.industryType}</FormFeedback>
                    ) : null}
                  </div>
                  <CustomSelectWithSearch
                    className={"form-select"}
                    placeholder='Select Industry Type'
                    validation={classnames({ "is-valid": validation.touched.industryType && !validation.errors.industryType, "is-invalid": validation.touched.industryType && validation.errors.industryType })}
                    options={industryType.map(item => { return { name: item, value: item } })}
                    onChange={(item) => { handleChange("industryType", item.name) }}
                    value={validation.values.industryType || ''}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col sm="12" md="12">
                <div className="mt-3">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="country" className="form-label">Country</Label>
                    {validation.touched.country && !validation.errors.country ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.country && validation.errors.country ? (
                      <FormFeedback type="invalid">{validation.errors.country}</FormFeedback>
                    ) : null}
                  </div>
                  <CustomSelectWithSearch
                    className={"form-select"}
                    placeholder='Select Country'
                    validation={classnames({ "is-valid": validation.touched.country && !validation.errors.country, "is-invalid": validation.touched.country && validation.errors.country })}
                    options={country}
                    onChange={(item) => { handleChange("country", item.name) }}
                    value={validation.values.country || ''}
                  />
                </div>
              </Col>
              {/* <Col sm="12" md="6">
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="city" className="form-label">City</Label>
                    {validation.touched.city && !validation.errors.city ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.city && validation.errors.city ? (
                      <FormFeedback type="invalid">{validation.errors.city}</FormFeedback>
                    ) : null}
                  </div>
                  <CustomSelect
                    className={"form-select"}
                    placeholder='Select City'
                    validation={classnames({ "is-valid": validation.touched.city && !validation.errors.city, "is-invalid": validation.touched.city && validation.errors.city })}
                    options={["Stockholm", "Oslo", "Helsinki", "London"].map(item => { return { name: item, value: item } })}
                    onChange={(item) => { handleChange("city", item.name) }}
                    value={validation.values.city || ''}
                  />
                </div>
              </Col> */}
            </Row>

            <Row>
              <Col sm="12" md="6">
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor='webSite'>Website</Label>
                    {validation.touched.webSite && !validation.errors.webSite ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.webSite && validation.errors.webSite ? (
                      <FormFeedback type="invalid">{validation.errors.webSite}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type='text' id="webSite" name='webSite'
                    placeholder='www.example.com'
                    className={"form-control " + classnames({ "is-valid": validation.touched.webSite && !validation.errors.webSite })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.webSite && validation.errors.webSite ? true : false}
                    value={validation.values.webSite}
                  />
                </div>

              </Col>
              <Col sm="12" md="6">
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor='contactEmail'>Contact Email</Label>
                    {validation.touched.contactEmail && !validation.errors.contactEmail ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.contactEmail && validation.errors.contactEmail ? (
                      <FormFeedback type="invalid">{validation.errors.contactEmail}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type='email' id="contactEmail" name='contactEmail'
                    placeholder='example@gmail.com'
                    className={"form-control " + classnames({ "is-valid": validation.touched.contactEmail && !validation.errors.contactEmail })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.contactEmail && validation.errors.contactEmail ? true : false}
                    value={validation.values.contactEmail}
                  />
                </div>
              </Col>
            </Row>

            {/* <Row>
              <Col sm="12" md="6">
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor='employeeCount'>Employee Count</Label>
                    {validation.touched.employeeCount && !validation.errors.employeeCount ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.employeeCount && validation.errors.employeeCount ? (
                      <FormFeedback type="invalid">{validation.errors.employeeCount}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type='number' id="employeeCount" name='employeeCount'
                    placeholder='10-50'
                    className={"form-control " + classnames({ "is-valid": validation.touched.employeeCount && !validation.errors.employeeCount })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.employeeCount && validation.errors.employeeCount ? true : false}
                    value={validation.values.employeeCount}
                  />
                </div>
              </Col>
              <Col sm="12" md="6">
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor='since'>Since</Label>
                    {validation.touched.since && !validation.errors.since ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.since && validation.errors.since ? (
                      <FormFeedback type="invalid">{validation.errors.since}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type='number' id="since" name='since'
                    placeholder={new Date().getFullYear()}
                    className={"form-control " + classnames({ "is-valid": validation.touched.since && !validation.errors.since })}
                    maxLength="4"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.since && validation.errors.since ? true : false}
                    value={validation.values.since}
                  />
                </div>
              </Col>
            </Row> */}

            <Row>
              <Col lg='12' md='12' sm='12'>
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor='information'>Information</Label>
                    {validation.touched.information && !validation.errors.information ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.information && validation.errors.information ? (
                      <FormFeedback type="invalid">{validation.errors.information}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type='textarea' name='information' id="information"
                    placeholder='Lorem ipsum dolor sit amet.'
                    className={'form-control ' + classnames({ "is-valid": validation.touched.information && !validation.errors.information })}
                    rows="2"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.information && validation.errors.information ? true : false}
                    value={validation.values.information}
                  ></Input>
                </div>
              </Col>
            </Row>

            <Row>
              <h5 className='mt-4 mb-1'>Invoicing Details</h5>
              <Col sm="12" md="6">
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor='billingName'>Billing Name</Label>
                    {validation.touched.billingName && !validation.errors.billingName ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.billingName && validation.errors.billingName ? (
                      <FormFeedback type="invalid">{validation.errors.billingName}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type='text' name='billingName' id='billingName'
                    className={'form-control ' + classnames({ "is-valid": validation.touched.billingName && !validation.errors.billingName })}
                    placeholder='Please enter billing name'
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.billingName && validation.errors.billingName ? true : false}
                    value={validation.values.billingName}
                  />
                </div>
              </Col>
              <Col sm="12" md="6">
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor='taxNumber'>Tax Number</Label>
                    {validation.touched.taxNumber && !validation.errors.taxNumber ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.taxNumber && validation.errors.taxNumber ? (
                      <FormFeedback type="invalid">{validation.errors.taxNumber}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type='text' name='taxNumber' id="taxNumber"
                    placeholder='Please enter company tax number'
                    className={'form-control ' + classnames({ "is-valid": validation.touched.taxNumber && !validation.errors.taxNumber })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.taxNumber && validation.errors.taxNumber ? true : false}
                    value={validation.values.taxNumber}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col sm="12" md="6">
                <div className='mt-3'>
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor='billingAddress'>Billing Adress</Label>
                    {validation.touched.billingAddress && !validation.errors.billingAddress ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.billingAddress && validation.errors.billingAddress ? (
                      <FormFeedback type="invalid">{validation.errors.billingAddress}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type='textarea' name='billingAddress' id="billingAddress"
                    placeholder='Please enter company billing address'
                    className={'form-control ' + classnames({ "is-valid": validation.touched.billingAddress && !validation.errors.billingAddress })}
                    rows="5"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.billingAddress && validation.errors.billingAddress ? true : false}
                    value={validation.values.billingAddress}
                  >
                  </Input>
                </div>
              </Col>
              <Col sm="12" md="6">
                <Row>
                  <Col lg={12} sm={12} md={12}>
                    <div className='mt-3'>
                      <div className='w-100 d-flex justify-content-between'>
                        <Label htmlFor='billingCountry'>Billing Country</Label>
                        {validation.touched.billingCountry && !validation.errors.billingCountry ? (
                          <FormFeedback valid>Looks good</FormFeedback>
                        ) : null}
                        {validation.touched.billingCountry && validation.errors.billingCountry ? (
                          <FormFeedback type="invalid">{validation.errors.billingCountry}</FormFeedback>
                        ) : null}
                      </div>
                      <CustomSelectWithSearch
                        className={"form-select"}
                        placeholder='Select Billing Country'
                        options={country}
                        onChange={(item) => { handleChange("billingCountry", item.name) }}
                        validation={classnames({ "is-valid": validation.touched.billingCountry && !validation.errors.billingCountry, "is-invalid": validation.touched.billingCountry && validation.errors.billingCountry })}
                        value={(validation.values.billingCountry || '')}
                      />
                    </div>
                  </Col>
                  <Col lg={12} sm={12} md={12}>
                    <div className='mt-3'>
                      <div className='w-100 d-flex justify-content-between'>
                        <Label htmlFor='phoneNumber'>Phone Number</Label>
                        {validation.touched.phoneNumber && !validation.errors.phoneNumber ? (
                          <FormFeedback valid>Looks good</FormFeedback>
                        ) : null}
                        {validation.touched.phoneNumber && validation.errors.phoneNumber ? (
                          <FormFeedback type="invalid">{validation.errors.phoneNumber}</FormFeedback>
                        ) : null}
                      </div>
                      <Input type='text' id="phoneNumber" name="phoneNumber"
                        placeholder='Please enter company phone number'
                        className={"form-control " + classnames({ "is-valid": validation.touched.phoneNumber && !validation.errors.phoneNumber })}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={validation.touched.phoneNumber && validation.errors.phoneNumber ? true : false}
                        value={validation.values.phoneNumber}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </React.Fragment>
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={toggleModal}>Close</button>
            <button type="submit" className="btn btn-success" id="add-btn" disabled={companyErr ? true : false}>
              Save Company
              {companyErr ? <Spinner size='sm' className='ms-2'>Loading...</Spinner> : null}
            </button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default CompanyEditModal;